import '@vendor/constructor-client'

const CONSTRUCTOR_CAMPAIGN_QSPS = [
  'g_adgroupid',
  'utm_term',
  'utm_campaign',
  'boost',
]

// Populated from facet.data.bands on the size facet (see normalizeFiterData)
let knownSizeBandKeys = null

/**
 * Existing links have a size valuew without a band (filter.size=8). Once Constructor's
 * size values have band-qualified, that existing value stops existing in the
 * index, so it's expanded to every banded variant whose size part matches
 * (size=8 -> size=["toddler:8", "kids:8", "womens:8", "mens:8"]).
 */
function expandLegacySizeFilters(filters) {
  if (!filters?.size || !knownSizeBandKeys) {
    return filters
  }

  const sizeValues = Array.isArray(filters.size) ? filters.size : [filters.size]

  const expanded = sizeValues.flatMap((value) => {
    if (typeof value !== 'string' || value.includes(':')) {
      return [value]
    }
    return knownSizeBandKeys.map((bandKey) => `${bandKey}:${value}`)
  })

  return { ...filters, size: expanded }
}

/**
 * A class leveraging standard frontend constructor client
 */
class ConstructorClient {
  /**
   * @param {string} indexKey - The Index Key
   */
  constructor(indexKey = __DNA_GLOBAL__?.constructor?.key) {
    this.client = new ConstructorioClient({
      apiKey: indexKey,
      segments: this.getSegments(),
      userId: getUserId(),
    })
  }

  get country() {
    // Prefetch loads before content_for_header so Shopify is not always available
    return window?.Shopify?.country ?? __DNA_GLOBAL__?.constructor?.country
  }

  getSegments() {
    const segments = [this.country]
    const queryParams = new URLSearchParams(new URL(window.location).search)
    const urlBasedCampaignParams = []
    for (const [key, value] of queryParams.entries()) {
      if (CONSTRUCTOR_CAMPAIGN_QSPS.includes(key.toLowerCase())) {
        urlBasedCampaignParams.push(value)
      }
    }

    if (urlBasedCampaignParams.length > 0) {
      try {
        window.sessionStorage.setItem(
          'constructor_segments',
          JSON.stringify(urlBasedCampaignParams),
        )
      } catch {}
    }

    // Get from session storage
    let storedSegments = []
    try {
      storedSegments = JSON.parse(
        window.sessionStorage.getItem('constructor_segments'),
      )
    } catch {}

    if (storedSegments && storedSegments.length > 0) {
      segments.push(...storedSegments)
    }

    return segments
  }

  get hiddenFields() {
    return [
      `price_${this.country}`,
      `compareprice_${this.country}`,
      `colorCountByMarket.${this.country}`,
    ]
  }

  get hiddenFacets() {
    return [`price_${this.country}`]
  }

  preFilterExpression(filters, arg_online_availability) {
    // If online_availability is set, respect it
    // Otherwise, if any filters are applied and one of them is size (and none are store_availability), only show items available online
    let online_availability = arg_online_availability
    if (online_availability === undefined) {
      const filterKeys = (filters && Object.keys(filters)) || []
      online_availability =
        filterKeys.length !== 0 &&
        !filterKeys.includes('store_availability') &&
        filterKeys.includes('size')
    }

    const andArray = [
      { name: 'published', value: 'True' },
      { name: 'countries_published', value: this.country },
    ]
    if (online_availability) {
      andArray.push({ name: 'online_availability', value: 'True' })
    }

    return {
      and: andArray,
    }
  }

  /**
   * Fetch Recommendations.
   * @param {string} pod_id - The Recommendations Pod ID.
   * @param {integer} numResults - Number of results returned.
   * @param {string} itemIds - The item ids.
   * @returns {Recommendation<Object>}
   */
  async getRecommendationPod(
    pod_id,
    { numResults = 20, filters, ...options },
    hideOOS = false,
  ) {
    filters = expandLegacySizeFilters(filters)
    const recommendationResults =
      await this.client.recommendations.getRecommendations(pod_id, {
        numResults,
        preFilterExpression: this.preFilterExpression(filters, hideOOS),
        filters,
        hiddenFacets: this.hiddenFacets,
        hiddenFields: this.hiddenFields,
        ...options,
      })
    if (recommendationResults) {
      const displayName = recommendationResults.response.pod.display_name

      const recommendationData = {
        display_name: displayName,
        result_id: recommendationResults.result_id,
        products: normalizeProductData(recommendationResults.response.results),
      }
      return recommendationData
    }
    return
  }

  /**
   * Fetch Recommendation Terms
   * @param {string} pod_d - The Recommendations Pod ID.
   * @param {integer} numResults - Number of results returned.
   * @param {string} itemIds - The item ids.
   * @returns {Recommendation<Object>}
   */
  async getRecommendationTerms(pod_id, { numResults = 20, ...options }) {
    const recommendationTermResults =
      await this.client.recommendations.getRecommendations(pod_id, {
        numResults,
        ...options,
      })
    if (recommendationTermResults) {
      const normalizeTermSuggestions = normalizeTermData(
        recommendationTermResults.response?.results,
      )
      const searchTermData = {
        searchTerms: normalizeTermSuggestions,
        resultId: recommendationTermResults?.result_id,
      }
      return searchTermData
    }
    return
  }

  /**
   * Fetch Collection.
   * @param {string} id - The Recommendations ID.
   * @returns {browse<Object>}
   */
  async getBrowseProducts(
    id,
    {
      filters = {},
      sortBy = 'relevance',
      sortOrder = 'descending',
      page = 1,
      resultsPerPage = __DNA_GLOBAL__?.constructor?.resultsPerPage,
    },
  ) {
    filters = expandLegacySizeFilters(filters)
    const browseData = await this.client.browse.getBrowseResults(
      'group_id',
      id,
      {
        preFilterExpression: this.preFilterExpression(filters),
        filters,
        hiddenFacets: this.hiddenFacets,
        hiddenFields: this.hiddenFields,
        sortBy,
        sortOrder,
        page,
        resultsPerPage,
      },
    )
    return normalizeBrowseAndSearchData(browseData, {
      isSearch: false,
    })
  }

  /**
   * Fetch Collection Filters.
   * @param {string} id - The Recommendations ID.
   * @param {integer} resultsPerPage - Results returned per page.
   * @returns {browse<Object>}
   */
  async getBrowseFilters(id, resultsPerPage) {
    const filters = await this.client.browse.getBrowseResults('group_id', id, {
      resultsPerPage,
      hiddenFacets: this.hiddenFacets,
      hiddenFields: this.hiddenFields,
    })
    return filters?.response?.facets
  }

  /**
   * Fetch Search Results.
   * Simple passthrough to the constructor client.
   */
  async getRawSearchResults(term, options) {
    return await this.client.search.getSearchResults(term, options)
  }

  /**
   * Fetch Search Results.
   * @param {string} term - The Search Term.
   * @param {integer} resultsPerPage - Results returned per page.
   * @returns {search<Object>}
   */
  async getSearchResults(
    term,
    {
      filters = {},
      sortBy = 'relevance',
      sortOrder = 'descending',
      page = 1,
      resultsPerPage = __DNA_GLOBAL__?.constructor?.searchResultsPerPage,
    },
  ) {
    filters = expandLegacySizeFilters(filters)
    const searchResults = await this.client.search.getSearchResults(term, {
      preFilterExpression: this.preFilterExpression(filters),
      filters,
      sortBy,
      sortOrder,
      page,
      resultsPerPage,
      hiddenFacets: this.hiddenFacets,
      hiddenFields: this.hiddenFields,
    })

    return normalizeBrowseAndSearchData(searchResults, {
      isSearch: true,
    })
  }

  async getAutocompleteProducts(query, numProducts, numSearchSuggestions) {
    if (!query) {
      return []
    }
    const response = await this.client.autocomplete.getAutocompleteResults(
      query,
      {
        resultsPerSection: {
          Products: numProducts,
          'Search Suggestions': numSearchSuggestions,
        },
        preFilterExpression: this.preFilterExpression(),
        hiddenFacets: this.hiddenFacets,
        hiddenFields: this.hiddenFields,
      },
    )
    const normalizeProducts = normalizeProductData(response?.sections?.Products)
    const normalizeSuggestions = normalizeTermData(
      response?.sections['Search Suggestions'],
    )
    const updatedResponse = {
      searchTerms: normalizeSuggestions,
      products: normalizeProducts,
    }
    return updatedResponse
  }
}

export const client = new ConstructorClient()

/**
 * Makes product data from Constructor.io workable with JS.
 * @param {Object[]} rawData - The response data from Constructor.io.
 * @returns {Object[]} The processed product data.
 */
function normalizeProductData(rawData) {
  if (!rawData || !Array.isArray(rawData)) {
    return []
  }
  return rawData.map((product) => {
    return {
      id: product?.data?.id,
      strategy_id: product?.strategy?.id,
      title: product?.value,
      handle: product?.data?.product_handle,
      onlineStoreUrl: product?.data?.url,
      sku: product?.data?.sku,
      featuredMedia: {
        altText: null,
        originalSrc: product?.data?.image_url,
      },
      media: normalizeMediaData(product?.data?.media),
      facets: product?.data?.facets,
      is_slotted: product?.is_slotted,
      result_id: product?.result_id,
      prices: extractPrefixedValues(product?.data, 'price_'),
      compareAtPrices: extractPrefixedValues(product?.data, 'compareprice_'),
      type: product?.data?.type,
      taxonomyCategory: product?.data?.taxonomy_category,
      barcode: product?.data?.barcode,
      color: product?.data?.color,
      giftcard: product?.data?.giftcard,
      variation_id: product?.data?.variation_id,
      variants: normalizeVariantData(product?.variations),
      groups: product?.data?.groups,
      metafields: {
        // mock data
        rothys: {
          badges: product?.data?.badges,
          maximum_order_quantity: product?.data?.maxorderquantity,
          preorder_backorder_experience:
            product?.data?.preorder_backorder_experience,
          estimated_ship_date: product?.data?.estimated_ship_date,
          return_rules: product?.data?.return_rules,
        },
        yotpo: {
          reviews_count: product?.data?.number_of_total_reviews,
          reviews_average: product?.data?.avg_review_rating,
        },
      },
      marketAvailability: extractPrefixedValues(product?.data, 'included_'),
      available: product?.data?.online_availability,
      // Missing fields compared to Shopify, set to null TODO: fill these in once data is available from Constructor
      description: null,
      minPrice: null,
      maxPrice: null,
      vendor: null,
      createdAt: null,
      updatedAt: null,
      launchedAt: product?.data?.launched_at || null,
      publishedAt: null,
      tags: [`storefront:category:${product?.data?.category}`],
      options: null,
      collections: null,
      isDiscounted: null,
      addOnItemsPresent: product?.data?.addOnItemsPresent || false,
      colorCountByMarket: product?.data?.colorCountByMarket || {},
      best_selling_30_days: product?.data?.best_selling_30_days || false,
      sales_velocity_7_days: product?.data?.sales_velocity_7_days,
      sales_velocity_30_days: product?.data?.sales_velocity_30_days,
      sales_velocity_24_hours: product?.data?.sales_velocity_24_hours,
      primary_badge: product?.data?.primary_badge,
      primary_badge_type: product?.data?.primary_badge_type,
      secondary_badge: product?.data?.secondary_badge,
      // @intelligems TECH-1516
      colorSwatches: product?.data?.colorSwatches || {},
      // END / TECH-1516
    }
  })
}

/**
 * Find any key with prefix in the code and return an array with all of those key values
 * @param {string} obj - The object to convert
 * @param {string} prefix - The prefix to search for
 * @returns {array}
 */
function extractPrefixedValues(obj, prefix) {
  if (!obj || typeof obj !== 'object') {
    return {}
  }
  const priceObj = {}

  for (const key in obj) {
    if (key.startsWith(prefix)) {
      const prefixKey = key.slice(prefix.length) // Remove 'prefix' prefix
      priceObj[prefixKey] = obj[key]
    }
  }

  return priceObj
}

/**
 * Convert object of objects to array of objects, removing keys.
 * @param {string} obj - The object to convert
 * @returns {array}
 */

function normalizeMediaData(obj) {
  const schema = {
    type: 'image',
    alt: '',
    url: '',
  }

  const normalizedObject = []

  for (const key in obj) {
    const entry = obj[key]
    if (!entry.alt_text.startsWith('PDP |')) {
      const normalizedEntry = {
        type: (entry.media_type || schema.type).toLowerCase(),
        alt: entry.alt_text || schema.alt,
        url: entry.url || schema.url,
      }
      normalizedObject.push(normalizedEntry)
    }
  }

  return normalizedObject
}

function normalizeVariantData(obj) {
  const schema = {
    title: '',
    available: false,
    id: '',
    sku: '',
    prices: [],
    compareAtPrices: [],
  }

  const normalizedObject = []

  for (const key in obj) {
    const entry = obj[key].data
    const normalizedEntry = {
      title: (entry.size || schema.title).toString(),
      available: entry.online_availability || schema.available,
      id: Number.parseInt(entry.variation_id || schema.id),
      sku: entry.sku,
      prices: extractPrefixedValues(entry, 'price_'),
      compareAtPrices: extractPrefixedValues(entry, 'compareprice_'),
      variant_position: entry.variant_position,
      width: entry.width || null,
      low_stock: entry.low_stock,
    }
    normalizedObject.push(normalizedEntry)
  }
  normalizedObject.sort((a, b) => a.variant_position - b.variant_position)

  return normalizedObject
}

const paginateBrowse = (browseResponse) => {
  // Use browseResponse to determine if there is a next page or previous page
  // broserResponse.response.total_num_results = total count
  // broserResponse.request.num_results_per_page = results per page
  // broserResponse.request.page = current page

  const total = browseResponse?.response?.total_num_results
  const perPage = browseResponse?.request?.num_results_per_page
  const currentPage = browseResponse?.request?.page

  const hasNext = total > currentPage * perPage
  const hasPrev = currentPage > 1

  return {
    page: currentPage,
    total: Math.ceil(total / perPage),
    hasNext,
    hasPrev,
    totalResults: total,
  }
}

const normalizeFiterData = (browseResponse) => {
  // Shape:
  // browseResponse?.response?.facets[]
  //     facet {
  //       data: {},
  //       display_name: <string>Name,
  //       hidden: Boolean,
  //       options: [
  //           count: no of Products
  //           data:{}
  //           display_name: <string>Name
  //           status: ""
  //       ]
  //     }

  return browseResponse?.response?.facets.map((facet) => {
    if (facet.name === 'size' && facet.data?.bands?.length) {
      knownSizeBandKeys = facet.data.bands.map((band) => band.key)
    }

    return {
      display_name: facet.display_name,
      name: facet.name,
      hidden: facet.hidden,
      type: facet.type,
      data: facet.data,
      options: facet?.options.map((option) => {
        return {
          count: option?.count,
          name: option?.display_name,
          value: option?.value,
          status: option?.status,
        }
      }),
      range: facet?.type === 'range' && {
        min: facet?.min,
        max: facet?.max,
      },
    }
  })
}

const normalizeSortOptionsData = (browseResponse) => {
  const selected = browseResponse?.response?.sort_options?.find(
    (option) => option?.status === 'selected',
  )

  return {
    options: browseResponse?.response?.sort_options,
    selected,
  }
}

const normalizeSearchandisingData = (browseResponse) => {
  return browseResponse?.response?.refined_content?.map((content) => {
    return {
      header_text: content.data.header,
      body_text: content.data.body,
      cta_text: content.data.ctaText,
      cta_link: content.data.ctaLink,
      second_cta_text: content.data.ctaText2,
      second_cta_link: content.data.ctaLink2,
      image_url: content.data.assetUrl,
      image_alt: content.data.altText,
      mobile_image_url: content.data.mobileAssetUrl,
      mobile_image_alt: content.data.mobileAssetAltText,
      tag_key: content.data.TagKey,
    }
  })
}

/**
 * Normalizes browse and search data from Constructor.io into a standardized format.
 *
 * @param {Object} results - The raw results object from Constructor.io.
 * * @param {boolean} [options.isSearch=false] - Indicates whether the results are from a search query (true) or browse
 * @returns {Object} An object containing normalized data with the following properties:
 *   @property {Object[]} products - Normalized product data.
 *   @property {Object} pagination - Pagination information.
 *   @property {Object[]} filters - Normalized filter data.
 *   @property {Object} sort - Normalized sort options data.
 *   @property {Object} meta - Metadata about the results.
 *   @property {(boolean|undefined)} redirect - Redirect information for search results, undefined for browse results.
 */
function normalizeBrowseAndSearchData(results, { isSearch = false } = {}) {
  return {
    products: normalizeProductData(results?.response?.results),
    searchandising: normalizeSearchandisingData(results),
    pagination: paginateBrowse(results),
    filters: normalizeFiterData(results),
    sort: normalizeSortOptionsData(results),
    meta: {
      totalCount: results?.response?.total_num_results || 0,
    },
    redirect: isSearch ? undefined : results?.response?.redirect || false,
    result_id: results?.result_id,
    request: results?.request,
  }
}

/**
 * Normalizes search suggestion data from Constructor.io into a standardized format.
 * @param {string} obj - The object to convert
 * @returns {array}
 */

function normalizeTermData(obj) {
  const schema = {
    id: '',
  }
  const normalizedObject = []

  for (const key in obj) {
    const entry = obj[key].data
    const normalizeEntry = {
      id: entry.id || schema.id,
    }
    normalizedObject.push(normalizeEntry)
  }
  return normalizedObject
}

function getUserId() {
  let userId = ''
  const rothysUserId = window.sessionStorage.getItem('rothys_user_id')
  if (rothysUserId) {
    userId = rothysUserId
  }
  return userId
}

/**
 *  Sets cnstrc-data script with updated request data and result idsß
 */
export function updateCnstrcDataScript({ request, result_id }) {
  if (!request || !result_id) {
    return
  }

  let script = document.getElementById('cnstrc-data')
  const result_request_json = JSON.stringify({ request, result_id })

  if (!script) {
    script = document.createElement('script')
    script.id = 'cnstrc-data'
    script.type = 'application/json'
    ;(document.body || document.head || document.documentElement).appendChild(
      script,
    )
  }

  script.textContent = result_request_json
}
